import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule, SwRegistrationOptions } from '@angular/service-worker';

import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireRemoteConfigModule, DEFAULTS, SETTINGS } from '@angular/fire/compat/remote-config';
import { AngularFireFunctionsModule, REGION } from '@angular/fire/compat/functions';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import * as Sentry from '@sentry/angular-ivy';

import { AppRoutingModule } from './app-routing.module';
import { MaterialModule } from './material.module';
import { environment } from '../environments/environment';


import { isiOS } from './utils';

// components
import { AppComponent } from './components/app.component';
import { LoginComponent } from './components/login/login.component';
import { ToolbarComponent } from './components/toolbar/toolbar.component';
import { HomeComponent } from './components/home/home.component';
import { StartupComponent } from './components/startup/startup.component';
import { HomeDeliveryDetailsComponent } from './components/home-delivery-details/home-delivery-details.component';
import { SettingsComponent } from './components/settings/settings.component';
import { TransportDetailsComponent } from './components/transport-details/transport-details.component';
import { TransportPlanComponent } from './components/transport-plan/transport-plan.component';
import { InfoBarComponent } from './components/info-bar/info-bar.component';
import { MessageComponent } from './components/message/message.component';
import { TourStopCardComponent } from './components/tour-stop-card/tour-stop-card.component';
import { MessageBarComponent } from './components/message-bar/message-bar.component';
import { ModalDialogComponent } from './components/modal-dialog/modal-dialog.component';
import { IncreaseItemCountDialogComponent } from './components/increase-item-count-dialog/increase-item-count-dialog.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { ScannerComponent } from "./components/scanner/scanner.component";
import { TourItemsBarComponent } from './components/tour-items-bar/tour-items-bar.component';
import { BatchScanningDialogComponent } from "./components/batch-scanning-dialog/batch-scanning-dialog.component";
import { LocationDeniedDialogComponent } from './components/location-denied-dialog/location-denied-dialog.component';


@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ToolbarComponent,
    HomeComponent,
    StartupComponent,
    HomeDeliveryDetailsComponent,
    SettingsComponent,
    TransportDetailsComponent,
    TransportPlanComponent,
    InfoBarComponent,
    MessageComponent,
    TourStopCardComponent,
    MessageBarComponent,
    ModalDialogComponent,
    IncreaseItemCountDialogComponent,
    ForgotPasswordComponent,
    ScannerComponent,
    TourItemsBarComponent,
    BatchScanningDialogComponent,
    LocationDeniedDialogComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,

    // ngx-translate
    TranslateModule.forRoot({
      defaultLanguage: 'de'
    }),

    // PWA support
    ServiceWorkerModule.register('ngsw-worker.js'),

    // Angular Material
    MaterialModule,

    // Firebase integration, see: https://github.com/angular/angularfire/blob/master/docs/install-and-setup.md
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAuthModule,
    AngularFireRemoteConfigModule,
    AngularFireFunctionsModule,
    AngularFirestoreModule,
    BrowserAnimationsModule
  ],
  providers: [
    // Firebase function region: https://github.com/angular/angularfire/blob/master/docs/functions/functions.md#functions-region
    { provide: REGION, useValue: 'europe-west6' },
    // configure remote config defaults, see: https://github.com/angular/angularfire/blob/master/docs/remote-config/getting-started.md
    {
      provide: DEFAULTS, useValue: {
        scannerInactivityTimeout: 0, // disabled
        resolutionPreference: 'full-hd',
        supportPhoneNumber: '0123456789',
        barcodeRegEx: '^[A-Z]{0,3}\\d{7,12}$',
        subsequentDeliveriesBarcode: 'TCS000001682',
        duplicateFilter: 10000
      }
    },
    // configure remote config settings, see: https://github.com/angular/angularfire/blob/master/docs/remote-config/getting-started.md
    {
      provide: SETTINGS, useFactory: () => ({
        minimumFetchIntervalMillis: 3_600_000 // 1 hour
      })
    },
    // configure service worker registration
    {
      provide: SwRegistrationOptions,
      useFactory: () => {
        return {
          enabled: environment.enableServiceWorker && window.location.hostname != 'localhost',
          registrationStrategy: 'registerImmediately'
        };
      }
    },
    // Sentry error reporting, see: https://docs.sentry.io/platforms/javascript/guides/angular
    {
      provide: ErrorHandler,
      useFactory: () => {
        if (environment.sentryDsn) {
          console.log(`Sentry error reporting enabled for environment: ${environment.name}`);
          return Sentry.createErrorHandler({
            showDialog: false,
            logErrors: true
          });
        } else {
          console.log(`Sentry not configured, using Angular default error handler (console)`);
          return new ErrorHandler();
        }
      }
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
